import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaFileExcel } from 'react-icons/fa';
import { showCurrency } from '../../utils';
import { useLabels } from '../../hooks/useLabels';
import { useTrailNavigate } from '../../context/EntityTrailContext';
import { AssetSaleDeletionModal } from '../../components/CascadeDeletionModal';
import { Section, StatStrip, DataTable, IdChip, RowActionsMenu, Button, ExportModal, buildXlsxExport, exportAmount } from '../../components/EntityList';
import { useClientSort } from '../../hooks/useClientSort';

function formatDate(date) {
	return date ? new Date(date).toLocaleDateString('es-UY') : '';
}

function formatMoney(currency, amount) {
	return `${showCurrency(currency) || ''} ${Number(amount || 0).toLocaleString('es-UY')}`.trim();
}

function AssetSales({ assetId, rows, loading, error, onRefetch }) {
	const { t } = useLabels();
	const navigate = useNavigate();
	const navigateWithTrail = useTrailNavigate();
	const [deletingAssetSale, setDeletingAssetSale] = useState(null);
	const [exportOpen, setExportOpen] = useState(false);

	const { sortedRows, sortColumn, direction, onSort } = useClientSort(rows, {
		defaultColumn: 'id',
		accessors: { id: (sale) => sale.customId },
	});

	const handleAddAssetSale = () => {
		navigate(`/AdminScreen/assets/asset/${assetId}/assetSale`);
	};

	const handleEditAssetSale = (assetSale) => {
		navigate(`/AdminScreen/assetSales/editAssetSale/${assetSale._id}`, {
			state: { returnTo: `/AdminScreen/assets/asset/${assetId}/sales` },
		});
	};

	const stats = useMemo(() => {
		const totalByCurrency = new Map();
		rows.forEach((sale) => {
			const key = sale.currency || '—';
			totalByCurrency.set(key, (totalByCurrency.get(key) || 0) + (sale.amount || 0));
		});
		return [
			{ label: t('entity.assetSale.plural'), value: rows.length },
			{
				label: t('field.amount'),
				value: [...totalByCurrency.entries()].map(([currency, total]) => formatMoney(currency, total)).join(' · ') || '—',
				mono: true,
			},
		];
	}, [rows, t]);


	const columns = [
		{ key: 'id', header: t('field.id'), sortable: true, render: (sale) => <IdChip>{sale.customId}</IdChip> },
		{ key: 'date', header: t('field.date'), render: (sale) => formatDate(sale.date) },
		{ key: 'lot', header: t('field.lotUpper'), className: 'tw:font-mono', render: (sale) => sale.lot },
		{
			key: 'amount',
			header: t('field.amount'),
			className: 'tw:text-right',
			render: (sale) => <span className='tw:font-mono tw:tabular-nums'>{formatMoney(sale.currency, sale.amount)}</span>,
		},
		{ key: 'purchaser', header: t('entity.purchaser'), render: (sale) => sale.purchaser?.name },
		{
			key: 'sumDebts',
			header: t('field.totalDebts'),
			className: 'tw:text-right',
			render: (sale) => <span className='tw:font-mono tw:tabular-nums'>{formatMoney(sale.currency, sale.sumDebts)}</span>,
		},
		{
			key: 'initialPayment',
			header: t('field.initialPayment'),
			className: 'tw:text-right',
			render: (sale) => <span className='tw:font-mono tw:tabular-nums'>{formatMoney(sale.currency, sale.initialPayment)}</span>,
		},
	];

	const exportFields = [
		{ key: 'id', label: t('field.id'), locked: true },
		{ key: 'date', label: t('field.date') },
		{ key: 'lot', label: t('field.lotUpper') },
		{ key: 'currency', label: t('field.currency') },
		{ key: 'amount', label: t('field.amount') },
		{ key: 'purchaser', label: t('entity.purchaser') },
		{ key: 'sumDebts', label: t('field.totalDebts') },
		{ key: 'initialPayment', label: t('field.initialPayment') },
	];

	const exportGetters = {
		id: (sale) => sale.customId || '',
		date: (sale) => formatDate(sale.date),
		lot: (sale) => sale.lot || '',
		currency: (sale) => sale.currency || '',
		amount: (sale) => exportAmount(sale.amount),
		purchaser: (sale) => sale.purchaser?.name || '',
		sumDebts: (sale) => exportAmount(sale.sumDebts),
		initialPayment: (sale) => exportAmount(sale.initialPayment),
	};

	const handleExport = (selectedKeys) =>
		buildXlsxExport({
			rows: sortedRows,
			fields: exportFields,
			selectedKeys,
			getters: exportGetters,
			sheetName: 'Ventas',
			filePrefix: 'ventas',
		});

	const renderRowActions = (sale) => (
		<RowActionsMenu
			items={[
				{ label: t('action.edit'), onClick: () => handleEditAssetSale(sale) },
				{ label: t('action.delete'), onClick: () => setDeletingAssetSale(sale), danger: true },
			]}
		/>
	);

	return (
		<div className='tw:flex tw:flex-col tw:gap-4'>
			<StatStrip stats={stats} />
			<div className='tw:flex tw:justify-end tw:gap-2'>
				<Button size='sm' icon={<FaFileExcel />} onClick={() => setExportOpen(true)} disabled={rows.length === 0}>
					{t('action.export')}
				</Button>
				<Button size='sm' variant='primary' onClick={handleAddAssetSale}>
					{t('action.add')} {t('entity.assetSale')}
				</Button>
			</div>
			<Section title={t('entity.assetSale.plural')}>
				<DataTable
					columns={columns}
					rows={sortedRows}
					loading={loading}
					error={error}
					sortColumn={sortColumn}
					direction={direction}
					onSort={onSort}
					onRowClick={(sale) => navigateWithTrail(`/AdminScreen/assetSales/assetSale/${sale._id}`)}
					rowActions={renderRowActions}
					emptyMessage={t('msg.noData')}
				/>
			</Section>

			<ExportModal
				show={exportOpen}
				onClose={() => setExportOpen(false)}
				count={rows.length}
				fields={exportFields}
				subtitle={t('entity.assetSale.plural')}
				onExport={handleExport}
			/>

			<AssetSaleDeletionModal
				show={Boolean(deletingAssetSale)}
				record={deletingAssetSale}
				onClose={() => setDeletingAssetSale(null)}
				onDeleted={() => {
					setDeletingAssetSale(null);
					onRefetch();
				}}
			/>
		</div>
	);
}

export default AssetSales;
